import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { installBaseService } from '../../../services/installBase.service';

const serviceCategories = [
  { value: 'warranty', label: 'Warranty' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'repair', label: 'Repair' },
  { value: 'inspection', label: 'Inspection' }
];

// Map alert categories to service categories
const mapAlertCategoryToServiceCategory = (alertCategory) => {
  const categoryMap = {
    'voltage': 'repair',
    'temperature': 'repair',
    'pressure': 'repair',
    'warranty': 'warranty',
    'maintenance': 'maintenance',
    'emergency': 'emergency',
    'repair': 'repair',
    'inspection': 'inspection'
  };

  return categoryMap[alertCategory?.toLowerCase()] || 'repair'; // Default to 'repair' if no mapping found
};

const priorities = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' }
];

const warrantyFlags = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

const ServiceRequestDialog = ({ 
  open, 
  onClose, 
  installationId, 
  alertId, 
  customerName,
  locationName,
  serialNumber, 
  requesterName: initialRequesterName 
}) => {
  const [formData, setFormData] = useState({
    ticket_no: '',
    request_type: '',
    location: locationName || '',
    asset_id: serialNumber || '',
    requester_name: initialRequesterName || '',
    parts_required: '',
    spare_part_request_linked_id: '',
    sla_target: '',
    warranty_flag: '',
    service_category: '',
    description: '',
    priority: '',
    request_date: new Date().toISOString().slice(0, 16),
    customer_name: customerName || ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Fetch alert details if alertId is provided
  useEffect(() => {
    const fetchAlertDetails = async () => {
      if (!open || !alertId) {
        setInitialLoading(false);
        return;
      }
      
      setInitialLoading(true);
      try {
        const alertData = await installBaseService.getAlertsByInstallation(installationId);
        const alert = alertData.find(a => a.alert_id === alertId);
        if (alert) {
          // Format the date
          const alertDate = new Date(alert.alert_date);
          const formattedDate = alertDate.toLocaleString();
          
          // Create enhanced description with timestamp and severity
          const enhancedDescription = `Alert Details:
Date: ${formattedDate}
Severity: ${alert.alert_severity}
Category: ${alert.alert_category}

Description:
${alert.description || ''}`;

          setFormData(prev => ({
            ...prev,
            description: enhancedDescription,
            priority: alert.alert_severity?.toLowerCase() || '',
            service_category: mapAlertCategoryToServiceCategory(alert.alert_category)
          }));
        }
      } catch (err) {
        console.error('Error fetching alert details:', err);
        setError('Failed to load alert data');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchAlertDetails();
  }, [open, alertId, installationId]);

  // Update form data when props change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      customer_name: customerName || '',
      location: locationName || '',
      asset_id: serialNumber || ''
    }));
  }, [customerName, locationName, serialNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // TODO: Implement API call to submit service request
      console.log('Submitting service request:', formData);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to submit service request');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Service Request</DialogTitle>
      <DialogContent>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Request submitted successfully!
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* First Row - Customer, Location, Asset */}
            <Grid item xs={12} md={4}>
              <TextField
                name="customer_name"
                label="Customer"
                value={formData.customer_name}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                name="location"
                label="Location"
                value={formData.location}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                name="asset_id"
                label="Asset ID / Serial No"
                value={formData.asset_id}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            </Grid>

            {/* Rest of the form */}
            <Grid item xs={12} md={4}>
              <TextField
                name="ticket_no"
                label="Ticket No"
                value={formData.ticket_no}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                name="request_type"
                label="Request Type"
                value={formData.request_type}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="requester_name"
                label="Requester Name"
                value={formData.requester_name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                name="parts_required"
                label="Parts Required"
                type="number"
                value={formData.parts_required}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="spare_part_request_linked_id"
                label="Spare Part Request Linked ID"
                value={formData.spare_part_request_linked_id}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                name="sla_target"
                label="SLA Target"
                value={formData.sla_target}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Warranty Flag</InputLabel>
                <Select
                  name="warranty_flag"
                  value={formData.warranty_flag}
                  onChange={handleChange}
                  label="Warranty Flag"
                >
                  {warrantyFlags.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Service Category</InputLabel>
                <Select
                  name="service_category"
                  value={formData.service_category}
                  onChange={handleChange}
                  label="Service Category"
                >
                  {serviceCategories.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priority"
                  required
                >
                  {priorities.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Full width fields */}
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description of Issue"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                margin="normal"
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="request_date"
                label="Request Date"
                type="datetime-local"
                value={formData.request_date}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceRequestDialog; 